import { useState, useEffect, useMemo, Fragment } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Trash2, Package, Briefcase, CalendarIcon, MapPin, ChevronDown, AlertTriangle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toastError } from '@/lib/toast';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { procurementTypeLabel } from '@/lib/procurementTypeLabel';
import { useAutoResizeTextarea, GROWING_TEXTAREA_CLASS } from '@/lib/autoResizeTextarea';
import { validateLineItems, classifyLineItems, computeLineSoftAlerts, uomQuantityType } from '@/lib/lineItemValidation';
import { createLineItem, dateFieldsForItemType } from '@/lib/lineItemDrafting';
import { highestSeverity, effectiveScheduleMode } from '@/lib/dateValidationRules';
import FieldError from '@/components/FieldError';
import CategorySelectCell from '@/components/CategorySelectCell';
import { CATEGORY_NO_FIT_KEY, buildCategoryNoFitDeclaration } from '@/lib/categoryNoFit';
import CodeNameLabel from '@/components/CodeNameLabel';
import LocationOptionLabel from '@/components/LocationOptionLabel';
import SeverityChip from '@/components/SeverityChip';

// Explicit goods/service choice at line addition (supply-and-install case,
// decided 2026-07-21): installation/training/demo lines need to exist
// alongside goods lines in an otherwise goods-typed procurement, and vice
// versa. A line's type is fixed at creation (delete and re-add to change
// it - no per-row toggle). In a pure goods or pure services procurement
// the matching type stays the one-click default; the alternate type is
// reachable via the dropdown. Mixed (or unset) procurements have no
// natural default, so both choices are shown with equal weight.
// THE DESCRIPTION FIELD MEASURES ITSELF MORE THAN ONCE (PROC-000412,
// 2026-09-04). It used to size itself in a ref callback at mount and never
// again; inside this table the description column narrows after mount
// (the category select and the location column claim width once their
// master data arrives), the longest descriptions rewrap to one more line,
// and a height set for the wider column hid the last line - "…confirmed
// per vendor product" with no "proposal." on lines 25 and 44 of a 50-row
// BOQ, complete in the database. A component of its own so the hook can
// own the ref: it re-measures on value change, on the element's own width
// change (ResizeObserver) and on window resize, and it carries the ruled
// ceiling class (max-h-[400px], overflow-y-auto) instead of
// overflow-hidden, so past the ceiling the field scrolls rather than cuts.
const DescriptionTextarea = ({ value, onChange, hasError, index }) => {
  const ref = useAutoResizeTextarea(value);
  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder="Product/Service description"
      className={`min-h-[38px] w-full text-sm bg-white py-1.5 ${GROWING_TEXTAREA_CLASS} ${hasError ? 'border-red-500 ring-1 ring-red-500' : ''}`}
      data-testid={`line-item-desc-${index}`}
    />
  );
};

const AddLineItemButton = ({ procurementType, onAdd }) => {
  if (procurementType === 'goods' || procurementType === 'services') {
    const primaryType = procurementType;
    const altType = primaryType === 'goods' ? 'services' : 'goods';
    const label = (type) => (type === 'goods' ? 'Add Goods Line' : 'Add Service Line');
    return (
      <div className="inline-flex">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAdd(primaryType)}
          className="border-zinc-200 rounded-r-none border-r-0"
          data-testid={`add-${primaryType}-line-btn`}
        >
          <Plus className="w-4 h-4 mr-1" />
          {label(primaryType)}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-200 rounded-l-none px-2"
              data-testid="add-line-item-alt-trigger"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAdd(altType)} data-testid={`add-${altType}-line-item`}>
              {label(altType)}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }
  return (
    <div className="inline-flex gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onAdd('goods')}
        className="border-zinc-200"
        data-testid="add-goods-line-btn"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Goods Line
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onAdd('services')}
        className="border-zinc-200"
        data-testid="add-services-line-btn"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Service Line
      </Button>
    </div>
  );
};

// Date Picker Component with validation support
const DatePickerField = ({ value, onChange, placeholder, minDate, maxDate, hasError }) => {
  const [open, setOpen] = useState(false);
  
  const handleSelect = (date) => {
    if (date) {
      // Stored/submitted value stays ISO (yyyy-MM-dd) - only the trigger
      // button's display text below uses the DD-MMM-YYYY format.
      onChange(format(date, 'yyyy-MM-dd'));
    }
    setOpen(false);
  };
  
  const parsedDate = value ? new Date(value) : undefined;
  const parsedMinDate = minDate ? new Date(minDate) : undefined;
  const parsedMaxDate = maxDate ? new Date(maxDate) : undefined;

  // Disable dates outside the valid range
  const disabledDays = (date) => {
    if (parsedMinDate && date < parsedMinDate) return true;
    if (parsedMaxDate && date > parsedMaxDate) return true;
    return false;
  };

  // Item 7 (date-validation-fixes batch, 2026-07-23): disable past-date
  // selection as a first line of defense - validation (HVND/HVSD/HVPT etc,
  // via the shared dateValidationRules) stays the backstop regardless.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-8 w-full justify-start text-left font-normal text-sm bg-white ${
            hasError ? 'border-red-500 ring-1 ring-red-500' : 'border-zinc-200'
          }`}
        >
          <CalendarIcon className={`mr-2 h-3 w-3 ${hasError ? 'text-red-500' : 'text-zinc-500'}`} />
          {value ? formatDate(value) : <span className="text-zinc-400">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsedDate}
          onSelect={handleSelect}
          disabled={[disabledDays, { before: startOfToday }]}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export const LineItemsEditor = ({
  lineItems,
  setLineItems,
  procurementType = 'mixed',
  shipToLocation = '',
  submissionDeadline = '',
  serverErrors = null,
  onValidationChange = null,
  // Date-validation-fixes batch (2026-07-23), items 3/4: reports the
  // computed per-line soft alerts up to StepLineItems.jsx - mirrors
  // onValidationChange's pattern exactly, so the Generate-click summary
  // dialog reads from the same computation this component's own status
  // column renders (single source of truth, not a second derivation).
  onSoftAlertsChange = null,
  assessedCategoryCode = null,
  // Unsaved-changes navigation guard (pre-merge item): fired on every add/
  // remove/field edit, never on a programmatic setLineItems (e.g. loading,
  // AI regeneration). Optional so this component stays usable outside the
  // wizard with no guard wiring.
  onDirty = null,
  // BOQ provenance (2026-07-30): {filename, uploaded_at, item_count} when
  // the current lines were seeded from an uploaded BOQ. null for legacy
  // documents/no BOQ - renders nothing (never a placeholder).
  boqSource = null,
  // THE PROPAGATION HINT (2026-09-16). Opt-in, because its words name the
  // "Regenerate from answers" control, which only StepLineItems renders.
  showPropagationHint = false,
  // Whether "Regenerate from answers" would be accepted (2026-09-17). False
  // while a header reference is stale - the regeneration is built from it and
  // is refused - so the hint does not recommend a refused action there.
  regenerateAvailable = true,
}) => {
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [uoms, setUoms] = useState([]);
  const [loadingUoms, setLoadingUoms] = useState(true);
  // Item 3: hover = preview the consolidated soft-alert message; click =
  // pin/unpin the same message beneath the row (touch-safe, stays visible
  // while editing that row's fields). Plain local state, not Radix
  // Tooltip/Popover - an inline "stays visible while editing" panel
  // doesn't fit their floating/dismiss-on-outside-click model cleanly.
  const [hoveredLine, setHoveredLine] = useState(null);
  const [pinnedLines, setPinnedLines] = useState(() => new Set());
  // The RETIRED codes, split from the same fetch as the active lists above.
  const [retiredCategoryCodes, setRetiredCategoryCodes] = useState(() => new Set());
  const [retiredLocationCodes, setRetiredLocationCodes] = useState(() => new Set());

  // Fetch locations on mount
  useEffect(() => {
    let isMounted = true;
    const fetchLocations = async () => {
      try {
        // ONE READ, FILTERED (2026-09-16). GET /master/locations returns every
        // row, active and retired; the picker's options (active) and the
        // retired codes the ship-to rule names as retired are split from THIS
        // one response, so the two sets can never disagree. It replaced
        // GET /locations (active-only, which could not tell a retired code
        // from a wrong one). A registered RULE 0b reader - see
        // routes/procurement.resolve_active_location_master's exception.
        const response = await api.getMasterLocations();
        if (isMounted) {
          const rows = Array.isArray(response.data) ? response.data : [];
          setLocations(rows
            .filter((row) => row && row.location_code && row.is_active !== false)
            .map((row) => ({ location_id: row.location_code, name: row.location_name, address: row.location_address })));
          setRetiredLocationCodes(new Set(rows
            .filter((row) => row && row.location_code && row.is_active === false)
            .map((row) => row.location_code)));
        }
      } catch (err) {
        // Locations failed to load - select will show empty list
        if (isMounted) {
          setLocations([]);
        }
      } finally {
        if (isMounted) {
          setLoadingLocations(false);
        }
      }
    };
    fetchLocations();
    return () => { isMounted = false; };
  }, []);
  
  // Fetch categories from master data on mount
  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        // ONE READ, FILTERED (2026-09-16). purpose=validation returns every row -
        // its docstring names "resolving a stored line item's spend_category",
        // which is this case. The dropdown's options (active - what
        // purpose=selection used to return) and the retired codes the category
        // rule names as retired come from THIS one response, never two.
        const response = await api.getMasterCategories('validation');
        const rows = isMounted && Array.isArray(response.data) ? response.data : [];
        const active = rows.filter((c) => c && c.is_active !== false);
        if (isMounted) {
          setRetiredCategoryCodes(new Set(rows
            .filter((c) => c && c.custom_code && c.is_active === false)
            .map((c) => c.custom_code)));
        }
        if (isMounted && active.length > 0) {
          setCategories(active);
        } else if (isMounted) {
          // No categories in master data - show error
          setCategories([]);
          toastError('No categories found. Please upload categories via Master Data Management.');
        }
      } catch (err) {
        // Categories failed to load - show error
        if (isMounted) {
          setCategories([]);
          toastError('Failed to load categories. Please check Master Data settings.');
        }
      } finally {
        if (isMounted) {
          setLoadingCategories(false);
        }
      }
    };
    fetchCategories();
    return () => { isMounted = false; };
  }, []);

  // Fetch UoMs from master data on mount (Item 2c, 2026-07-21 sweep) -
  // replaces the old hardcoded UOM_OPTIONS list.
  useEffect(() => {
    let isMounted = true;
    const fetchUoms = async () => {
      try {
        const response = await api.getMasterUoms('selection');
        if (isMounted && response.data && response.data.length > 0) {
          setUoms(response.data);
        } else if (isMounted) {
          setUoms([]);
          toastError('No UoMs found. Please upload UoM master via Master Data Management.');
        }
      } catch (err) {
        if (isMounted) {
          setUoms([]);
          toastError('Failed to load UoM master. Please check Master Data settings.');
        }
      } finally {
        if (isMounted) {
          setLoadingUoms(false);
        }
      }
    };
    fetchUoms();
    return () => { isMounted = false; };
  }, []);

  // Defect A fix (2026-07-21): ONE classification pass, shared by every
  // consumer below (date-layout selection, category filtering, AND
  // validation) - see lineItemValidation.js's classifyLineItems doc
  // comment. Index-aligned with lineItems.
  const lineItemTypes = useMemo(
    () => classifyLineItems(lineItems, procurementType),
    [lineItems, procurementType]
  );

  // Hard validation (BACKLOG.md Batch 1, blocker 2026-07-21): recomputed on
  // every edit/add, in-scope lines only (modified===true or source==='user').
  // While categories are still loading, categoryCodes is empty and
  // validateLineItems skips the master-data-existence check (falls back to
  // just "required") rather than flagging every category as unknown.
  const categoryCodes = useMemo(
    () => new Set(categories.map((c) => c.custom_code).filter(Boolean)),
    [categories]
  );
  // Item 2d (2026-07-21 sweep) - same empty-set-skips-the-check convention
  // as categoryCodes above, while uoms is still loading.
  const uomCodes = useMemo(
    () => new Set(uoms.map((u) => u.uom_name).filter(Boolean)),
    [uoms]
  );
  // The ACTIVE ship-to codes (GET /locations is active-only), for the ship-to
  // rule (2026-09-16). Same empty-set-skips convention while loading.
  const locationCodes = useMemo(
    () => new Set(locations.map((l) => l.location_id).filter(Boolean)),
    [locations]
  );
  // uom_name -> quantity_type, for the whole-number rule (2026-08-24).
  // Built from the SAME `uoms` state the dropdown renders - GET
  // /master/uoms?purpose=selection returns the full row and the admin-only
  // strip pops measurement_family/unit_semantics ONLY, so quantity_type is
  // present for every caller, admin or not.
  //
  // A unit missing from this map resolves to null in uomQuantityType, and
  // null never blocks. That is why the map is built by OMISSION - a row
  // without a quantity_type (a master uploaded before 7aed7be) simply is
  // not in it. See lib/lineItemValidation's uomQuantityType for why that
  // helper is NOT statedIntentValidationRules' findUomSemantics: this
  // lookup's fail-safe runs the opposite way, and the two must not be
  // "aligned".
  const uomQuantityTypes = useMemo(
    () => Object.fromEntries(
      uoms.filter((u) => u.uom_name && u.quantity_type)
        .map((u) => [u.uom_name, u.quantity_type])
    ),
    [uoms]
  );
  const lineErrors = useMemo(
    () => validateLineItems(lineItems, { procurementType, submissionDeadline, categoryCodes, retiredCategoryCodes, uomCodes, uomQuantityTypes, locationCodes, retiredLocationCodes }, lineItemTypes),
    [lineItems, procurementType, submissionDeadline, categoryCodes, retiredCategoryCodes, uomCodes, uomQuantityTypes, locationCodes, retiredLocationCodes, lineItemTypes]
  );
  // Backend errors (routes/_line_item_validation.py) are the authoritative
  // backstop for a stale client - merged in alongside the live client-side
  // computation so they render identically; client message wins if both
  // flag the same field, since it reflects the current (possibly already
  // corrected) value.
  const mergedErrors = useMemo(() => {
    if (!serverErrors || serverErrors.length === 0) return lineErrors;
    const merged = lineErrors.map((e) => ({ ...e }));
    serverErrors.forEach(({ line_index, field, message }) => {
      if (!merged[line_index]) merged[line_index] = {};
      if (!merged[line_index][field]) merged[line_index][field] = message;
    });
    return merged;
  }, [lineErrors, serverErrors]);

  useEffect(() => {
    if (onValidationChange) onValidationChange(mergedErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedErrors]);

  // Items 3/4 (date-validation-fixes batch, 2026-07-23): per-line soft
  // alerts (SANT/SAST/SAPN/SAPS - need-by/start close to today or to
  // submission) - computed once here, rendered as this component's own
  // status column AND reported up for StepLineItems' Generate-click
  // summary dialog. Submission-vs-today (SAPT) is intentionally excluded
  // (procurement-wide, not line-specific - surfaced once near StepLineItems'
  // submission reference box instead).
  const lineSoftAlerts = useMemo(
    () => computeLineSoftAlerts(lineItems, { procurementType, submissionDeadline }, lineItemTypes),
    [lineItems, procurementType, submissionDeadline, lineItemTypes]
  );
  const softAlertsByLineIndex = useMemo(() => {
    const map = {};
    lineSoftAlerts.forEach((entry) => { map[entry.lineIndex] = entry; });
    return map;
  }, [lineSoftAlerts]);

  useEffect(() => {
    if (onSoftAlertsChange) onSoftAlertsChange(lineSoftAlerts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineSoftAlerts]);

  // ONE renumbering rule, extracted from removeLineItem (which has applied
  // it since 2026-07) and now shared with addLineItem. line_number is
  // DERIVED from array position - never carried forward, never incremented
  // locally - so an insert and a removal cannot disagree about what "line
  // 3" means. Extracting it rather than writing a second copy is the point:
  // two renumbering expressions would be an unregistered mirror pair.
  const renumberLines = (items) => items.map((item, i) => ({ ...item, line_number: i + 1 }));

  // INSERT BY TYPE, THEN RENUMBER (Ashok ruling, 2026-08-23, superseding
  // the numbering half of the 2026-08-22 ruling). A new line is inserted
  // AFTER THE LAST LINE OF ITS OWN TYPE, then the whole array renumbers
  // 1..N in array order - so a 9-goods + 1-service document gains its new
  // goods line as line 10 at the foot of the Goods section, and the service
  // line becomes 11. Goods 1-10, services 11-12: the array order, the
  // section order and the RFP's Schedule A / Schedule B all agree.
  //
  // Appending to the end (the pre-F18 behaviour) put the new goods line at
  // number 11 while rendering it under line 9, because F18 groups the rows
  // by type but the numbers came from array position.
  //
  // MEMBERSHIP IS lineItemTypes - the same shared classifyLineItems pass
  // that decides which SECTION a line renders in. Reading anything else
  // here (item_type raw, the category flag) could land the new line in a
  // position that its own section does not contain.
  //
  // VISIBLE CONSEQUENCE, accepted by the ruling: adding a goods line
  // renumbers every service line below it, so a buyer watching the screen
  // sees "line 10" become "line 11". That is correct and is what the RFP
  // does - the alternative is a document whose numbers do not match its
  // own schedules.
  const addLineItem = (type) => {
    // No line of this type yet -> append at the end, which is also where
    // its brand-new section will render (goods above services).
    const lastOfType = lineItemTypes.lastIndexOf(type);
    const insertAt = lastOfType === -1 ? lineItems.length : lastOfType + 1;
    const next = [...lineItems];
    next.splice(insertAt, 0, createLineItem({ type, lineNumber: insertAt + 1, shipToLocation }));
    // Renumbered IMMEDIATELY, in local state, on the click - the same live
    // behaviour removeLineItem has always had. Deferring it to save would
    // show the buyer a document whose numbers contradict its own ordering
    // until they navigated away.
    setLineItems(renumberLines(next));
    onDirty?.();
  };

  const removeLineItem = (index) => {
    setLineItems(renumberLines(lineItems.filter((_, i) => i !== index)));
    onDirty?.();
  };
  
  const updateLineItem = (index, field, value) => updateLineItemFields(index, { [field]: value });

  /**
   * ONE UPDATE, N FIELDS (F1 / C1b-1b). updateLineItem above delegates
   * here, so every rule below runs exactly as it always did for a
   * single-field edit.
   *
   * WHY IT EXISTS. Declaring a no-fit changes TWO fields at once - it sets
   * the record and clears the category - and two updateLineItem calls in
   * one handler both read `lineItems` from the same closure, so the second
   * silently discards the first. The two facts are also one fact: a line
   * cannot both carry a category and say that none of them fit, and
   * writing them separately is precisely how they would come to disagree.
   *
   * A field whose value is `undefined` is DELETED rather than set to
   * undefined - no value means no key, and a key holding undefined would
   * travel to the server as nothing useful and sit there forever.
   */
  const updateLineItemFields = (index, fields) => {
    const updated = [...lineItems];
    const item = { ...updated[index] };
    Object.entries(fields).forEach(([key, val]) => {
      if (val === undefined) {
        delete item[key];
      } else {
        item[key] = val;
      }
    });
    const [field, value] = Object.entries(fields)[0] || [null, undefined];

    // Message-type sweep (item 1): an end-before-start date was previously
    // rejected here with a blocking toastError - a decision/validation
    // outcome routed through the transient-toast system instead of inline
    // field display, and one that silently discarded the user's input
    // (early return, value never stored) so the inline error could never
    // even render. validateLineItem (lib/lineItemValidation.js) already
    // computes this exact rule with a richer message (values embedded,
    // scope-aware framing) - the value is now always stored like any other
    // field, and hasDateError/rowErrors.start_date/end_date below render it
    // inline, same as every other line-item validation rule.

    // EXECUTION SCHEDULE MODEL (Ashok's design, 2026-07-26; generalized to
    // GOODS lines by the MIXED-SEMANTICS + ECHO FIX BATCH, item C): typing
    // a date into any date field of a VENDOR_PROPOSED line (need_by_date
    // for goods, start_date/end_date for services) is the ONE action that
    // flips it back to BUYER_DEFINED - there is no separate toggle control,
    // the mode is earned by supplying a date. Once flipped, normal date
    // validation (presence/HV rules) applies as for any other
    // buyer-defined line.
    if (
      (field === 'start_date' || field === 'end_date' || field === 'need_by_date') &&
      value &&
      item.schedule_mode === 'VENDOR_PROPOSED'
    ) {
      item.schedule_mode = 'BUYER_DEFINED';
    }

    // NO-FIT IS A DECLARATION, NOT A LOCK (F1 / C1b-1b, Ashok ruling).
    // Choosing a category withdraws the request - and reselection is the
    // ONLY route out, since a disposition never unblocks the buyer. Placed
    // here, beside the schedule_mode transition above, because it is the
    // same shape of rule: a one-way state change EARNED by supplying a
    // value, with no separate toggle control to forget to press.
    //
    // DELETED, NOT BLANKED. A line carrying `category_no_fit: null` would
    // read as absent through the shared reader anyway, but it would also
    // travel to the server and sit in the document forever as a key that
    // means nothing. No value means no key.
    if (field === 'spend_category' && value && item[CATEGORY_NO_FIT_KEY]) {
      delete item[CATEGORY_NO_FIT_KEY];
    }

    // Provenance (spec P2 §5/§10.3): editing a field on an AI- or
    // BOQ-sourced line marks it modified - a user-added line is already
    // flagged via source:'user' at creation and doesn't need this.
    if (item.source === 'ai' || item.source === 'boq') {
      item.modified = true;
    }

    updated[index] = item;
    setLineItems(updated);
    onDirty?.();
  };
  
  // Check if a line item has date validation error
  const hasDateError = (item) => {
    if (!item.start_date || !item.end_date) return false;
    return new Date(item.end_date) < new Date(item.start_date);
  };
  
  // Get location display for table
  const getLocationDisplay = (locationId) => {
    const loc = locations.find(l => l.location_id === locationId);
    return loc ? `${loc.location_id} - ${loc.name}` : locationId;
  };

  // F18 (2026-08-23) - SECTION MEMBERSHIP: MAP FIRST, THEN FILTER.
  // The values row was 1136px minimum under the old mixed layout (three
  // date columns, two of them inert on any given row) and overflowed a
  // laptop viewport. Split by type, each section carries ONE fixed date
  // layout - 636px for goods, 764px for services - so EmptyDateCell and
  // isMixedDateLayout are DELETED rather than narrowed: no row's date
  // columns fail to apply to it any more, which is the only thing those
  // two existed for.
  //
  // Every entry keeps the line's ORIGINAL index into lineItems, and that
  // is the whole safety property of this change. removeLineItem(index),
  // updateLineItem(index, ...), mergedErrors[index],
  // softAlertsByLineIndex[index], lineItemTypes[index] and
  // CategorySelectCell's sibling lookup (usedCategoryCodes(lineItems,
  // index)) are all addressed by the DATA index, and every data-testid is
  // suffixed with it. Nothing in this component or its children is
  // addressed by a row's position within its table, so splitting the rows
  // across two tables re-points none of them.
  //
  // Membership is lineItemTypes[index] - the ONE shared classification
  // pass (Defect A fix, 2026-07-21), i.e. effectiveItemType. Reading the
  // SAME pass that the date layout and validation read is what guarantees
  // a line can never sit in one section while being validated as the other
  // type. A line whose item_type is ABSENT therefore lands wherever
  // effectiveItemType already put it - for a goods or mixed procurement
  // with no start/end dates, GOODS. That existing default is now visible
  // as section placement (Ashok ruling 3, 2026-08-22); no new rule is
  // introduced for it. Measured at the time of the ruling: zero line
  // elements lack item_type in either environment.
  //
  // Ruling 4 (Ashok, 2026-08-22): A CATEGORY EDIT MUST NOT MOVE A LINE
  // BETWEEN SECTIONS - and it cannot, because effectiveItemType never
  // reads spend_category. item_type classifies the WORK, the category flag
  // classifies the SPEND CATEGORY, and a supply-and-install line
  // legitimately carries service-nature work under a goods code (12.4% of
  // prod lines disagree, and most disagreements are correct). Moving such
  // a line on a category edit would also silently change its date shape
  // and orphan the dates already stored on it.
  const sections = useMemo(() => {
    const goods = [];
    const services = [];
    lineItems.forEach((item, index) => {
      (lineItemTypes[index] === 'services' ? services : goods).push({ item, index });
    });
    return { goods, services };
  }, [lineItems, lineItemTypes]);

  // DERIVED, NOT REPEATED (F17's rule, kept and re-based on the section's
  // type). Three body rows span a whole section table (the separator, the
  // schedule-note row and the soft-alert panel) and the description cell
  // spans part of another, so the counts are computed once here from the
  // same value that drives that section's <thead>.
  //
  // FIXED_COLUMN_COUNT is every non-date column: #, Qty, UoM, the
  // Ship To / Category column, the timing-concern icon, and the remove
  // control. It is SIX, not F17's seven, because Category no longer has a
  // column of its own - it renders in the LOCATION column's position on
  // the description row, which is why splitting the table costs no width
  // back. Description is not among them either; it owns row 1 outright.
  const FIXED_COLUMN_COUNT = 6;
  // Composed from dateFieldsForItemType (lib/lineItemDrafting.js) so the
  // number of date COLUMNS a section renders cannot drift from the number
  // of date FIELDS its rows render.
  const dateColumnCountFor = (sectionType) => dateFieldsForItemType(sectionType).length;
  const totalColumnCountFor = (sectionType) => FIXED_COLUMN_COUNT + dateColumnCountFor(sectionType);
  // Row 1's description spans Qty, UoM and this section's date column(s);
  // the #, Category, timing-icon and remove cells hold the rest of row 1.
  const descriptionColSpanFor = (sectionType) => 2 + dateColumnCountFor(sectionType);

  // ONE row-block renderer, shared by both sections and parameterized by
  // the section's type - not two copies of the markup. Two copies would be
  // an unregistered mirror pair with nothing holding them together, and
  // the numeric-input census (components/ui/number-input.census.test.jsx)
  // counts this file's NumberInput render sites against a literal 1.
  const renderLineBlock = ({ item, index }, sectionType, positionInSection) => {
    const rowErrors = mergedErrors[index] || {};
    const showServiceDates = dateFieldsForItemType(sectionType).includes('start_date');
    const totalColumnCount = totalColumnCountFor(sectionType);
    const descriptionColSpan = descriptionColSpanFor(sectionType);
    return (
      <Fragment key={`li-${item.line_number}-${index}`}>
        {/* SEPARATOR (F17 ruling c, 2026-08-22): a deliberate element, not
            incidental margin. With two rows per line an undelimited tbody
            reads as one flat grid of alternating wide/narrow rows, and the
            reader loses which value row belongs to which description.
            Rendered BETWEEN the lines OF THIS SECTION only - gated on the
            position within the section, not on the data index, so a
            section whose first line is not lineItems[0] gains no stray
            rule above it. Named for the line BELOW it, by DATA index, so
            the testid keeps meaning exactly what it meant. */}
        {positionInSection > 0 && (
          <tr aria-hidden="true" data-testid={`line-separator-${index}`}>
            <td colSpan={totalColumnCount} className="p-0">
              <div className="h-0.5 bg-zinc-300" />
            </td>
          </tr>
        )}
        {/* ROW 1 OF 2 - the line number, the description, the category and
            the remove control. The # and remove cells carry rowSpan={2}
            (Ashok's mockup, 2026-08-22): both address the LINE, not either
            row of it. */}
        <tr className="hover:bg-zinc-50/50" data-testid={`line-desc-row-${index}`}>
          {/* GAP CLOSURE (2026-08-23): pl-3 pr-0, not px-3. The gap between
              the number and the description box was two td paddings back to
              back - this cell's 12px right padding plus the description
              cell's 12px left padding - so the box started 24px from the
              number's cell edge. Padding only; the # column keeps its w-12
              header width, and the number keeps its 12px left inset so it
              still sits under the "#" label. */}
          <td rowSpan={2} className="pl-3 pr-0 pt-3 pb-3 text-zinc-500 font-medium align-top">
            {item.line_number}
          </td>
          <td colSpan={descriptionColSpan} className="pl-0 pr-3 pt-3 pb-1 align-top">
            {/* F17: was a single-line <Input> forced to h-8. The
                refinement agent writes specification, finish and warranty
                into this field, so a buyer confirming a line could not see
                what they were confirming. Grows with its content via the
                shared autoResizeTextarea helper - ref callback for text
                that arrives already long, onChange for typing - with
                resize-none/overflow-hidden so there is no inner scrollbar
                and no drag handle competing with the automatic sizing.
                jsdom reports scrollHeight as 0, so the GROWTH is not
                executable by the hermetic suite; what the suite asserts is
                that this is a textarea wired to the helper. */}
            <DescriptionTextarea
              value={item.description}
              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
              hasError={Boolean(rowErrors.description)}
              index={index}
            />
            <FieldError message={rowErrors.description} />
          </td>
          {/* CATEGORY (F18, 2026-08-23) - rendered in the LOCATION COLUMN'S
              POSITION, on the description row. It reads beside the text it
              classifies, and it costs no new width because it shares that
              column with Ship To Location instead of adding one of its
              own. */}
          <td colSpan={2} className="pl-3 pr-0 pt-3 pb-1 align-top">
            <CategorySelectCell
              value={item.spend_category || ''}
              onChange={(value) => updateLineItem(index, 'spend_category', value)}
              description={item.description}
              index={index}
              lineItems={lineItems}
              categories={categories}
              loadingCategories={loadingCategories}
              assessedCategoryCode={assessedCategoryCode}
              fieldError={rowErrors.spend_category}
              /* NO-FIT (F1 / C1b-1b). The subject IS the line - the record
                 hangs on it as a sibling of spend_category, so the picker
                 reads it from the same object it writes back to. Declaring
                 clears the category in the SAME update: a line cannot both
                 carry a category and say that none fits, and writing the
                 two separately is how they would come to disagree. */
              noFitSubject={item}
              onDeclareNoFit={(text) => {
                const declaration = buildCategoryNoFitDeclaration(text);
                if (!declaration) return;
                updateLineItemFields(index, {
                  [CATEGORY_NO_FIT_KEY]: declaration,
                  spend_category: '',
                });
              }}
              onClearNoFit={() => updateLineItemFields(index, { [CATEGORY_NO_FIT_KEY]: undefined })}
            />
          </td>
          {/* GAP CLOSURE (2026-08-23), the second gap. This row used to
              carry an EMPTY aria-hidden spacer cell here, holding open the
              whole timing-concern column (w-10) between the category select
              and the delete control - which, with the two td paddings on
              either side of it, is the ~64px gap that was reported. The
              category cell above now colSpans that column instead, and its
              select (w-full from SelectTrigger) fills the width, so the
              category ends flush against the delete control.
              NO COLUMN CHANGES WIDTH: the timing-concern column is still
              sized by its own w-10 header and still holds the alert icon on
              the VALUES row, where the icon belongs - beside the date
              fields that provoke it. Only this row's cell composition
              changed, which is why the descRow anchor moves 5 -> 4. */}
          {/* REMOVE CONTROL (F17 ruling d, 2026-08-22). It removes a LINE,
              not a row, so it belongs to the row that NAMES the line rather
              than to the tail of the value grid - and it now spans both
              rows for that same reason. It keeps its own column and its
              unchanged remove-line-item testid, so no consumer moves. */}
          <td rowSpan={2} className="pl-0 pr-3 pt-3 pb-3 align-top text-right">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeLineItem(index)}
              className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500"
              data-testid={`remove-line-item-${index}`}
              title="Remove this line"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </td>
        </tr>
        {/* ROW 2 OF 2 - the remaining fields, each under its header. */}
        <tr className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`line-values-row-${index}`}>
          {/* pl-0 (2026-08-23): the description box and the Qty box both
              sit in this column's left edge, but the description cell lost
              its left padding in F19's gap closure while this one kept
              px-3 - so the two boxes started 12px apart, the description
              flush and Qty indented. Zeroing the same side here lines the
              BOXES up, which is what the reader sees; the header <th>
              takes the same pl-0 so "Quantity" stays above its own box.
              No width class changes: the table is w-full, so the 12px this
              column stops reserving is redistributed to the description
              column rather than narrowing the table. */}
          <td className="pl-0 pr-3 pb-3 align-top">
            <NumberInput
              /* `?? ''` keeps this a CONTROLLED input when quantity is
                 null - which it now is whenever the BOQ cell could not be
                 resolved (Ashok ruling, 2026-08-19). Rendering null
                 directly makes React switch the field to uncontrolled and
                 warn; rendering '' shows the buyer an empty box to fill,
                 which is the confirmation the ruling asks for. The stored
                 value stays null until they type. */
              value={item.quantity ?? ''}
              onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
              /* w-28 (112px), up from w-24's 96px. The input's own px-3
                 leaves 88px of content where it left 72 - enough for a
                 seven-figure quantity without clipping. Its header's
                 declared width is 112 + this cell's 12px pr-3 gutter; the
                 two must move together. */
              className={`h-8 text-sm bg-white w-28 ${rowErrors.quantity ? 'border-red-500 ring-1 ring-red-500' : ''}`}
              min="0"
              /* step FROM THE ROW'S OWN UNIT (2026-08-24), never a
                 constant. `step` alone controls nothing in this app -
                 there is no <form> around these inputs and nothing calls
                 reportValidity(), so it produces no message on its own;
                 the inline FieldError below is the control. It is set
                 correctly anyway because a browser extension surfaced
                 Chromium's native stepMismatch on "4.8 Kg", which is a
                 LEGITIMATE decimal - a per-unit step makes any such native
                 message accurate instead of noise.
                 UNDEFINED when the unit is unknown, deliberately: omitting
                 the attribute is the permissive state, whereas step="1"
                 would assert whole-only about a unit nobody classified. */
              step={
                uomQuantityType(item.uom, uomQuantityTypes) === 'integer' ? '1'
                  : uomQuantityType(item.uom, uomQuantityTypes) === 'decimal' ? 'any'
                    : undefined
              }
              data-testid={`line-item-qty-${index}`}
            />
            <FieldError message={rowErrors.quantity} />
          </td>
          <td className="px-3 pb-3 align-top">
            <Select
              value={item.uom}
              onValueChange={(value) => updateLineItem(index, 'uom', value)}
            >
              <SelectTrigger
                className={`h-8 text-sm bg-white ${rowErrors.uom ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                data-testid={`uom-select-trigger-${index}`}
              >
                {/* Item 2f (2026-07-21 sweep): explicit children so the
                    trigger ALWAYS renders the stored value, even when it
                    isn't in uom_master (legacy/variant spelling) - the root
                    cause of the reported bug was the default SelectValue
                    rendering blank for an unmatched value. */}
                <SelectValue placeholder="Select UoM">
                  {item.uom || undefined}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {loadingUoms ? (
                  <SelectItem value="__loading__" disabled>Loading UoMs...</SelectItem>
                ) : (
                  uoms
                    .filter((u) => u.applies_to === 'both' || u.applies_to === sectionType)
                    .map((u) => (
                      <SelectItem key={u.uom_code || u.uom_name} value={u.uom_name}>{u.uom_name}</SelectItem>
                    ))
                )}
                {/* Item 2f: the current stored value stays selectable even
                    when it's not in the master list. */}
                {item.uom && !uoms.some((u) => u.uom_name === item.uom) && (
                  <SelectItem value={item.uom}>{item.uom} (current)</SelectItem>
                )}
              </SelectContent>
            </Select>
            <FieldError message={rowErrors.uom} />
          </td>
          {showServiceDates ? (
            <>
              <td className="px-3 pb-3 align-top">
                <DatePickerField
                  value={item.start_date || ''}
                  onChange={(date) => updateLineItem(index, 'start_date', date)}
                  placeholder="Start date"
                  maxDate={item.end_date}
                  hasError={hasDateError(item) || Boolean(rowErrors.start_date)}
                />
                <FieldError message={rowErrors.start_date} />
              </td>
              <td className="px-3 pb-3 align-top">
                <DatePickerField
                  value={item.end_date || ''}
                  onChange={(date) => updateLineItem(index, 'end_date', date)}
                  placeholder="End date"
                  minDate={item.start_date}
                  hasError={hasDateError(item) || Boolean(rowErrors.end_date)}
                />
                <FieldError message={rowErrors.end_date} />
              </td>
            </>
          ) : (
            <td className="px-3 pb-3 align-top">
              <DatePickerField
                value={item.need_by_date || ''}
                onChange={(date) => updateLineItem(index, 'need_by_date', date)}
                placeholder="Select date"
                hasError={Boolean(rowErrors.need_by_date)}
              />
              <FieldError message={rowErrors.need_by_date} />
            </td>
          )}
          <td className="px-3 pb-3 align-top">
            <Select
              value={item.ship_to_location || ''}
              onValueChange={(value) => updateLineItem(index, 'ship_to_location', value)}
            >
              <SelectTrigger
                className="h-8 text-sm bg-white"
                data-testid={`ship-to-select-trigger-${index}`}
              >
                {/* F16 (2026-08-18) - the SAME fix the UoM column received
                    on 2026-07-21, applied here at last. Explicit children
                    so the trigger ALWAYS renders the stored value, even
                    when it is absent from the active-only option list; the
                    default SelectValue renders BLANK for an unmatched
                    value, which is how a line carrying a deactivated
                    location came to look empty on resume (PROC-000352).
                    The value was never lost - only unshowable.

                    CODE - NAME (2026-08-23), through the SAME CodeNameLabel
                    the category cell directly above uses. A bare location
                    code told a buyer nothing about which site they were
                    shipping to, and the two controls share this column, so
                    they now share its idiom.

                    THE UNMATCHED ARM NAMES NO SITE: GET /locations is
                    active-only, so a line carrying a DEACTIVATED location
                    has no matching entry and THERE IS NO NAME TO SHOW -
                    "NOI001 - " with an empty tail would be worse than the
                    code by itself.

                    THE CLOSED CELL IS MARKED (2026-09-16 - SUPERSEDES the
                    2026-08-23 ruling on this point). That ruling kept the
                    marker inside the open dropdown only, which was right
                    while an unmatched location was informational. It now
                    BLOCKS - finalize_line_items refuses it and the
                    master-reference guard refuses the next boundary - so
                    the closed cell carries the SAME treatment as the
                    category cell beside it: amber icon and
                    "CODE (update required)", with the ship-to rule's inline
                    error beneath. The list option keeps its own
                    "not in current list" annotation; the trigger still does
                    not repeat that text.

                    NOT BEFORE THE LIST LOADS: locations is [] until
                    getLocations resolves, so every stored code would
                    otherwise flash the marker on every mount. The bare
                    code renders until then. */}
                <SelectValue placeholder="Select location">
                  {item.ship_to_location
                    ? (() => {
                        // Normalised exactly as the ship-to rule normalises
                        // (trim + upper), so the marker and the inline error
                        // can never disagree about the same value.
                        const wanted = String(item.ship_to_location).trim().toUpperCase();
                        const loc = locations.find((l) => String(l.location_id || '').trim().toUpperCase() === wanted);
                        if (loc) return <CodeNameLabel code={loc.location_id} name={loc.name} />;
                        if (loadingLocations) return item.ship_to_location;
                        return (
                          <span
                            className="truncate text-xs flex items-center gap-1 text-amber-600"
                            data-testid={`ship-to-update-required-${index}`}
                          >
                            <AlertCircle className="w-3 h-3" />
                            {item.ship_to_location} (update required)
                          </span>
                        );
                      })()
                    : undefined}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {loadingLocations ? (
                  <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                ) : (
                  // MATCH THE ANSWERS STEP (2026-08-23): the SAME
                  // LocationOptionLabel that step's picker uses - CODE -
                  // Name with the address as a muted second line - rather
                  // than a second renderer that looks like it. Both
                  // pickers read the same GET /locations payload, which has
                  // always carried `address`; this one was simply not
                  // rendering it, so a buyer choosing a site in the Lines
                  // editor saw less than the same buyer choosing one on
                  // Answers.
                  //
                  // The TRIGGER above is deliberately NOT changed: it keeps
                  // the compact CODE - Name pair, which is the same rule
                  // the Answers step's trigger follows (UI POLISH ruling,
                  // 2026-07-28, item 2) and for the same reason - Radix
                  // would otherwise mirror this option's full content,
                  // address included, into the closed control.
                  locations.map(loc => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>
                      <LocationOptionLabel code={loc.location_id} name={loc.name} address={loc.address} />
                    </SelectItem>
                  ))
                )}
                {/* The stored value stays SELECTABLE even when it is not in
                    the master list, matching the UoM column. WORDING
                    DELIBERATELY DIFFERS FROM UoM'S "(current)". The options
                    here are active-only and location codes come from the
                    master, so an unmatched value means the row is NOT
                    ACTIVE - "(current)" would invite reading "currently
                    valid", which is exactly what it is not. UoM's unmatched
                    case is a legacy/variant spelling, where "(current)"
                    reads correctly. Same idiom, honest label. */}
                {item.ship_to_location
                  && !locations.some((loc) => loc.location_id === item.ship_to_location) && (
                  <SelectItem value={item.ship_to_location}>
                    <div className="flex flex-col">
                      <span className="font-medium">{item.ship_to_location}</span>
                      <span className="text-xs text-amber-700">not in current list</span>
                    </div>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {/* The ship-to rule's inline error (2026-09-16), the same
                FieldError the category cell's rule renders. */}
            <FieldError message={rowErrors.ship_to_location} />
          </td>
          <td className="px-3 pb-3 text-center align-top">
            {softAlertsByLineIndex[index] && (
              <button
                type="button"
                className="text-amber-500 hover:text-amber-600"
                onMouseEnter={() => setHoveredLine(index)}
                onMouseLeave={() => setHoveredLine((prev) => (prev === index ? null : prev))}
                onClick={() => setPinnedLines((prev) => {
                  const next = new Set(prev);
                  if (next.has(index)) next.delete(index); else next.add(index);
                  return next;
                })}
                data-testid={`soft-alert-icon-${index}`}
                title="Timing concern - hover or tap for details"
              >
                <AlertTriangle className="w-4 h-4" />
              </button>
            )}
          </td>
        </tr>
        {/* MIXED-SEMANTICS + ECHO FIX BATCH (item C): no longer gated to
            itemType 'services' - schedule_mode generalizes to goods lines
            in MIXED (see NEED-BY IS REQUIREMENT-LEVEL IN MIXED, agents.py's
            refinement_agent date_rules). */}
        {effectiveScheduleMode(item) === 'VENDOR_PROPOSED' && (
          <tr className="bg-blue-50/50 border-b border-zinc-100">
            <td colSpan={totalColumnCount} className="px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-blue-700 whitespace-nowrap">Schedule note:</span>
                <Input
                  value={item.schedule_note || ''}
                  onChange={(e) => updateLineItem(index, 'schedule_note', e.target.value)}
                  placeholder="Vendor shall propose an execution schedule consistent with the required completion date."
                  className={`h-8 text-sm bg-white flex-1 ${rowErrors.schedule_note ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                  data-testid={`schedule-note-input-${index}`}
                />
              </div>
              <FieldError message={rowErrors.schedule_note} />
            </td>
          </tr>
        )}
        {softAlertsByLineIndex[index] && (hoveredLine === index || pinnedLines.has(index)) && (
          <tr className="bg-amber-50 border-b border-zinc-100">
            <td colSpan={totalColumnCount} className="px-3 py-2 text-xs text-amber-700">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5" />
              {/* SOFT-ALERT SEVERITY BATCH (2026-07-25): the hover/pin panel
                  carries a chip for the line's single most severe
                  contributing code - alerts themselves are already
                  severity-ordered by computeLineSoftAlerts, so the
                  plain-text message reads most-severe-first too. */}
              <SeverityChip
                severity={highestSeverity(softAlertsByLineIndex[index].alerts.map((a) => a.code))}
                className="mr-1.5"
              />
              {/* Generate-summary polish (2026-07-27): consolidatedMessage
                  joins multiple contributing sentences with '\n' -
                  whitespace-pre-line is what actually turns that into a
                  visual line break (plain text otherwise collapses it). */}
              <span className="whitespace-pre-line align-top">{softAlertsByLineIndex[index].consolidatedMessage}</span>
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  // ONE section = one table with its own TWO-ROW header, mirroring the
  // two-row body block exactly: header row A labels the description row's
  // cells (with #, the timing-concern column and the remove column
  // spanning down into row B, matching the body's rowSpan={2} cells), and
  // header row B labels the values row's fields. Category's <th> therefore
  // sits directly above Ship To Location's - the shared column that is what
  // makes this split cost no width.
  //
  // Header row A has FIVE cells while the body's description row now has
  // FOUR (gap closure, 2026-08-23): the body's category cell colSpans the
  // timing-concern column, where the header keeps a separate rowSpan={2}
  // <th> for it. Both still reconstruct to the same totalColumns, which is
  // what the anchors assert - the per-row counts are deliberately NOT
  // asserted as equal to each other.
  const renderSection = (sectionType, entries) => {
    const isServices = sectionType === 'services';
    const descriptionColSpan = descriptionColSpanFor(sectionType);
    return (
      <div className="overflow-x-auto" data-testid={`lines-section-${sectionType}`}>
        <div
          className="flex items-center gap-1.5 mb-1 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500"
          data-testid={`lines-section-heading-${sectionType}`}
        >
          {isServices ? <Briefcase className="w-3.5 h-3.5" /> : <Package className="w-3.5 h-3.5" />}
          {isServices ? 'Services' : 'Goods'}
        </div>
        <table className="w-full text-sm border border-zinc-200">
          <thead className="bg-zinc-50">
            <tr>
              <th rowSpan={2} className="px-3 py-2 text-left font-medium text-zinc-700 border-b border-zinc-200 w-12">#</th>
              {/* pl-0 mirrors the body cell's gap closure below, so the
                  label still sits directly above its textarea's left edge
                  rather than 12px to the right of it. */}
              <th colSpan={descriptionColSpan} className="pl-0 pr-3 py-2 text-left font-medium text-zinc-700">Description</th>
              <th className="px-3 py-2 text-left font-medium text-zinc-700 w-36">Category</th>
              <th rowSpan={2} className="px-3 py-2 text-center font-medium text-zinc-700 border-b border-zinc-200 w-10" title="Timing concerns"></th>
              <th rowSpan={2} className="px-3 py-2 text-left font-medium text-zinc-700 border-b border-zinc-200 w-12"></th>
            </tr>
            <tr>
              {/* pl-0 mirrors the body cell below (2026-08-23), same as
                  Description's. "Quantity" spelled out: the abbreviation
                  bought nothing - the header only ever needed to fit inside
                  the column, not inside the word.

                  THE DECLARED WIDTH NOW EQUALS THE NATURAL DEMAND, which is
                  what F20 flagged and left open. This <th> declared w-24
                  (96px) while the body cell below demanded its input
                  (w-24, 96px) plus its own pr-3 gutter (12px) = 108px, so
                  the column rendered 12px wider than anything declared it
                  and the next person doing width arithmetic would have
                  read 96. The input widens to w-28 (112px) and this
                  declares 112 + 12 = 124px, so the two agree by
                  construction. Keep them in step: the invariant is
                  asserted in LineItemsEditor.test.jsx against literals. */}
              <th className="pl-0 pr-3 py-2 text-left font-medium text-zinc-700 border-b border-zinc-200 w-[124px]">Quantity</th>
              <th className="px-3 py-2 text-left font-medium text-zinc-700 border-b border-zinc-200 w-28">UoM</th>
              {isServices ? (
                <>
                  <th className="px-3 py-2 text-left font-medium text-zinc-700 border-b border-zinc-200 w-40">
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" />
                      Start Date
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-zinc-700 border-b border-zinc-200 w-40">
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" />
                      End Date
                    </div>
                  </th>
                </>
              ) : (
                <th className="px-3 py-2 text-left font-medium text-zinc-700 border-b border-zinc-200 w-40">
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" />
                    Need By Date
                  </div>
                </th>
              )}
              <th className="px-3 py-2 text-left font-medium text-zinc-700 border-b border-zinc-200 min-w-[180px]">
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Ship To Location
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, positionInSection) => renderLineBlock(entry, sectionType, positionInSection))}
          </tbody>
        </table>
      </div>
    );
  };

  // LINES THAT CARRY A STALE REFERENCE (2026-09-16): a non-blank category or
  // ship-to that is not in the ACTIVE list. Evaluated only once BOTH lists
  // have loaded - an empty list means "not known yet", never "everything is
  // stale" - and only for the hint below; the line errors are the rule's.
  const staleReferenceLineCount = (loadingCategories || loadingLocations || categoryCodes.size === 0 || locationCodes.size === 0)
    ? 0
    : lineItems.filter((item) => {
        const category = item.spend_category ? String(item.spend_category).trim() : '';
        const shipTo = item.ship_to_location ? String(item.ship_to_location).trim().toUpperCase() : '';
        const activeShipTo = new Set([...locationCodes].map((c) => String(c).trim().toUpperCase()));
        return (category && !categoryCodes.has(category)) || (shipTo && !activeShipTo.has(shipTo));
      }).length;

  return (
    <div className="space-y-4">
      {/* LINES DO NOT FOLLOW THE HEADER (2026-09-16). A header change - a
          category replaced at Intake, answers edited - does NOT rewrite the
          lines already here: they are buyer-confirmed content, and silently
          rewriting them is the PROC-000317 substitution the staleness gate
          exists to prevent. "Regenerate from answers" is the deliberate
          propagation path. Shown only while a line carries a stale reference,
          so it never nags on a clean schedule. */}
      {showPropagationHint && staleReferenceLineCount > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900"
          role="status"
          data-testid="lines-propagation-hint"
        >
          <p className="font-medium">
            {staleReferenceLineCount === 1
              ? '1 line uses a category or ship-to location that is not active in the master.'
              : `${staleReferenceLineCount} lines use a category or ship-to location that is not active in the master.`}
          </p>
          {regenerateAvailable ? (
            <p className="mt-1 text-amber-800" data-testid="lines-propagation-hint-remedy">
              Lines keep what was confirmed here - changing the category or your answers on an earlier step does not update them.
              Fix those lines below, or use &ldquo;Regenerate from answers&rdquo; to rebuild all lines from your current answers
              (this replaces your line edits).
            </p>
          ) : (
            <p className="mt-1 text-amber-800" data-testid="lines-propagation-hint-remedy">
              Lines keep what was confirmed here - changing the category or your answers on an earlier step does not update them.
              Fix those lines below. &ldquo;Regenerate from answers&rdquo; will work once the category on the Intake step and the
              ship-to location on the Answers step are active - it rebuilds the lines from them.
            </p>
          )}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">
            Requirements Schedule
          </div>
          <p className="text-sm text-zinc-600">
            {procurementType === 'goods' && <><Package className="w-4 h-4 inline mr-1" /> Goods - Specify Need By Date</>}
            {procurementType === 'services' && <><Briefcase className="w-4 h-4 inline mr-1" /> Services - Specify Start/End Dates</>}
            {/* TYPE LABEL PARITY PATCH (2026-07-28 ruling): "Mixed - Goods &
                Services" was its own one-off label, diverging from the
                uniform vocabulary (procurementTypeLabel) used everywhere
                else on screen and in the .docx. */}
            {procurementType === 'mixed' && <>{procurementTypeLabel('mixed')}</>}
          </p>
          {/* Provenance disclosure (2026-07-30): same wording as the
              Intake step's FileUploadSection.jsx line - renders nothing
              when boqSource is absent (legacy document/no BOQ).

              SPLIT WITH ITS TWIN (2026-08-21). The line carried the count
              as well as the filename; on Intake that repeated a heading
              already stating it, so the count moved out. THIS copy is
              changed with them because its comment above declares it the
              same wording, and one fact should not have two sentences
              across three screens. The count is not lost here - the
              table-header count below still carries it. */}
          {boqSource && (
            <p className="text-xs text-zinc-500 mt-1" data-testid="boq-provenance-line">
              Source: {boqSource.filename}
            </p>
          )}
          {/* Item 3: table-header count - a lightweight signal that
              something needs a look, without permanently rendering every
              line's concern (that's what the per-row icon + summary dialog
              are for). */}
          {lineSoftAlerts.length > 0 && (
            <p className="text-xs text-amber-600 font-medium mt-1" data-testid="soft-alerts-header-count">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {lineSoftAlerts.length} line{lineSoftAlerts.length === 1 ? '' : 's'} have timing concerns
            </p>
          )}
        </div>
        <AddLineItemButton procurementType={procurementType} onAdd={addLineItem} />
      </div>
      
      {/* TWO SECTIONS, STACKED (F18, 2026-08-23). EMPTY SECTIONS DO NOT
          RENDER: a goods-only document shows the Goods table and nothing
          else - no empty Services table, no "no service lines" placeholder.
          An empty section reads as something the buyer failed to fill in,
          and it would spend the vertical space this change exists to
          reclaim. Nothing is lost by omitting it, because the ADD control
          sits OUTSIDE both sections (Ashok ruling 2, 2026-08-22) and keeps
          today's goods/service choice, so a service line stays one click
          away in a goods-only procurement - which is 50% of staging and
          63% of prod. When BOTH are empty the existing "No line items added
          yet" panel below is what renders. */}
      <div className="space-y-6">
        {sections.goods.length > 0 && renderSection('goods', sections.goods)}
        {sections.services.length > 0 && renderSection('services', sections.services)}
      </div>
      
      {lineItems.length === 0 && (
        <div className="text-center py-8 bg-zinc-50 border border-zinc-200 rounded-sm">
          <p className="text-zinc-500 mb-3">No line items added yet</p>
          <div className="flex justify-center">
            <AddLineItemButton procurementType={procurementType} onAdd={addLineItem} />
          </div>
        </div>
      )}
      
      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Rate and Extended Price columns will be added to the RFP for vendors to fill in. 
          Extended Price = Quantity × Rate (calculated by vendor).
        </p>
      </div>
    </div>
  );
};

export default LineItemsEditor;
